import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { testLoaderService } from '../services/testLoaderService';
import { progressStore } from '../services/progressStore';
import { TestData, TestAttempt } from '../types/testy';
import { Spinner } from './LoadingComponents';

interface TestStartPageProps {
  testId: string;
  onNavigate: (section: string, testId?: string) => void;
}

const TestStartPage: React.FC<TestStartPageProps> = ({ testId, onNavigate }) => {
  const { currentUser } = useAuth();
  const [testData, setTestData] = useState<TestData | null>(null);
  const [existingAttempt, setExistingAttempt] = useState<TestAttempt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTestData = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);

        // Load test data
        const data = await testLoaderService.loadTest(testId);
        setTestData(data);

        // Check for existing incomplete attempt
        const attempt = await progressStore.loadProgress(testId, currentUser.uid);
        setExistingAttempt(attempt);
      } catch (err) {
        console.error('Failed to load test data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load test data');
      } finally {
        setLoading(false);
      }
    };

    loadTestData();
  }, [testId, currentUser]);

  const handleStartTest = () => {
    onNavigate('test-runner', testId);
  };

  const handleResumeTest = () => {
    onNavigate('test-runner', testId);
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours} h ${minutes} min`;
    }
    return `${minutes} min`;
  };

  const calculateQuestionCounts = (data: TestData) => {
    const sectionCounts = data.sections.map(section => {
      const partCounts = section.parts.map(part => part.questions.length);
      const sectionTotal = partCounts.reduce((sum, count) => sum + count, 0);
      return {
        title: section.title,
        parts: section.parts.map((part, index) => ({
          title: part.title,
          count: partCounts[index]
        })),
        total: sectionTotal
      };
    });

    const totalQuestions = sectionCounts.reduce((sum, section) => sum + section.total, 0);

    return { sectionCounts, totalQuestions };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">⚠️</div>
        <h2 className="text-2xl font-semibold text-red-600 dark:text-red-400 mb-2 font-poppins">
          Chyba při načítání testu
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins mb-4">
          {error}
        </p>
        <button
          onClick={() => onNavigate('test-list')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200"
        >
          Zpět na seznam testů
        </button>
      </div>
    );
  }

  if (!testData) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">❓</div>
        <h2 className="text-2xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
          Test nenalezen
        </h2>
        <p className="text-light-text-secondary dark:text-dark-text-secondary font-poppins mb-4">
          Požadovaný test nebyl nalezen nebo není dostupný.
        </p>
        <button
          onClick={() => onNavigate('test-list')}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white rounded-lg font-poppins font-medium transition-colors duration-200"
        >
          Zpět na seznam testů
        </button>
      </div>
    );
  }

  const { sectionCounts, totalQuestions } = calculateQuestionCounts(testData);

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      {/* Header */}
      <header className="mb-8">
        <nav className="mb-4">
          <button
            onClick={() => onNavigate('test-list')}
            className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-poppins focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 rounded-lg p-1"
            aria-label="Zpět na seznam testů"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span>Zpět na seznam testů</span>
          </button>
        </nav>
        
        <h1 className="text-2xl sm:text-3xl font-bold text-light-text-primary dark:text-dark-text-primary mb-2 font-poppins">
          {testData.title}
        </h1>
        
        {testData.isFreeSample && (
          <div 
            className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
            role="status"
            aria-label="Tento test je dostupný zdarma"
          >
            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span>Ukázkový test zdarma</span>
          </div>
        )}
      </header>

      {/* Resume existing attempt */}
      {existingAttempt && (
        <div 
          className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6"
          role="alert"
          aria-labelledby="existing-attempt-title"
        >
          <div className="flex items-start">
            <svg 
              className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 mr-3 flex-shrink-0" 
              fill="currentColor" 
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div className="flex-1 min-w-0">
              <h3 
                id="existing-attempt-title"
                className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1"
              >
                Máte rozpracovaný pokus
              </h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mb-3">
                Zbývající čas: {formatDuration(existingAttempt.timeRemaining)}
              </p>
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <button
                  onClick={handleResumeTest}
                  className="px-4 py-2 bg-yellow-600 hover:bg-yellow-700 focus:bg-yellow-700 text-white rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 dark:focus:ring-offset-yellow-900"
                  aria-describedby="existing-attempt-title"
                >
                  Pokračovat v testu
                </button>
                <button
                  onClick={handleStartTest}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 focus:bg-gray-700 text-white rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 dark:focus:ring-offset-yellow-900"
                  aria-describedby="existing-attempt-title"
                >
                  Začít znovu
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
        {/* Test Overview */}
        <div className="space-y-6">
          <section 
            className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            aria-labelledby="test-overview-title"
          >
            <h2 
              id="test-overview-title"
              className="text-lg sm:text-xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 font-poppins"
            >
              Přehled testu
            </h2>
            
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-light-text-secondary dark:text-dark-text-secondary">Časový limit:</span>
                <span className="font-medium text-light-text-primary dark:text-dark-text-primary">
                  {formatDuration(testData.durationSec)}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-light-text-secondary dark:text-dark-text-secondary">Celkem otázek:</span>
                <span className="font-medium text-light-text-primary dark:text-dark-text-primary">
                  {totalQuestions}
                </span>
              </div>
              
              <div className="flex justify-between">
                <span className="text-light-text-secondary dark:text-dark-text-secondary">Počet sekcí:</span>
                <span className="font-medium text-light-text-primary dark:text-dark-text-primary">
                  {testData.sections.length}
                </span>
              </div>
            </div>
          </section>

          {/* Section Breakdown */}
          <section 
            className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            aria-labelledby="test-structure-title"
          >
            <h3 
              id="test-structure-title"
              className="text-lg font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 font-poppins"
            >
              Struktura testu
            </h3>
            
            <div className="space-y-4">
              {sectionCounts.map((section, index) => (
                <div key={index} className="border-l-4 border-blue-500 pl-4">
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-light-text-primary dark:text-dark-text-primary">
                      {section.title}
                    </h4>
                    <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                      {section.total} otázek
                    </span>
                  </div>
                  <div className="space-y-1">
                    {section.parts.map((part, partIndex) => (
                      <div key={partIndex} className="flex justify-between text-sm">
                        <span className="text-light-text-secondary dark:text-dark-text-secondary">
                          {part.title}
                        </span>
                        <span className="text-light-text-secondary dark:text-dark-text-secondary">
                          {part.count} otázek
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Instructions and Rules */}
        <div className="space-y-6">
          <section 
            className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            aria-labelledby="scoring-rules-title"
          >
            <h2 
              id="scoring-rules-title"
              className="text-lg sm:text-xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 font-poppins"
            >
              Pravidla hodnocení
            </h2>
            
            <div className="space-y-3">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                <span className="text-light-text-secondary dark:text-dark-text-secondary">
                  Správná odpověď: <strong className="text-green-600 dark:text-green-400">+{testData.scoring.correct} bod</strong>
                </span>
              </div>
              
              <div className="flex items-center">
                <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                <span className="text-light-text-secondary dark:text-dark-text-secondary">
                  Špatná odpověď: <strong className="text-red-600 dark:text-red-400">{Math.round(testData.scoring.wrong * 10) / 10} bodu</strong>
                </span>
              </div>
              
              <div className="flex items-center">
                <div className="w-3 h-3 bg-gray-400 rounded-full mr-3"></div>
                <span className="text-light-text-secondary dark:text-dark-text-secondary">
                  Nevyplněno: <strong className="text-gray-600 dark:text-gray-400">{testData.scoring.blank} bodů</strong>
                </span>
              </div>
            </div>
          </section>

          <section 
            className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            aria-labelledby="instructions-title"
          >
            <h2 
              id="instructions-title"
              className="text-lg sm:text-xl font-semibold text-light-text-primary dark:text-dark-text-primary mb-4 font-poppins"
            >
              Instrukce
            </h2>
            
            <ul className="space-y-3 text-light-text-secondary dark:text-dark-text-secondary">
              <li className="flex items-start">
                <svg 
                  className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Každá otázka má právě jednu správnou odpověď z možností A, B, C, D</span>
              </li>
              
              <li className="flex items-start">
                <svg 
                  className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Můžete se volně pohybovat mezi otázkami a měnit své odpovědi</span>
              </li>
              
              <li className="flex items-start">
                <svg 
                  className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Otázky můžete označit pro pozdější kontrolu</span>
              </li>
              
              <li className="flex items-start">
                <svg 
                  className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span>Váš pokrok se automaticky ukládá během testu</span>
              </li>
              
              <li className="flex items-start">
                <svg 
                  className="w-5 h-5 text-orange-500 mt-0.5 mr-3 flex-shrink-0" 
                  fill="currentColor" 
                  viewBox="0 0 20 20"
                  aria-hidden="true"
                >
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>Test se automaticky odešle po uplynutí časového limitu</span>
              </li>
            </ul>
          </section>

          {/* Start Button */}
          <section 
            className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700"
            aria-labelledby="start-test-title"
          >
            {!existingAttempt ? (
              <>
                <h2 id="start-test-title" className="sr-only">Začít test</h2>
                <button
                  onClick={handleStartTest}
                  className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:bg-blue-600 text-white rounded-lg font-poppins font-semibold text-lg transition-colors duration-200 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                  aria-describedby="test-overview-title scoring-rules-title instructions-title"
                >
                  <svg 
                    className="w-6 h-6 mr-2" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" strokeWidth={2} />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14s1.5 2 4 2 4-2 4-2" />
                    <circle cx="9" cy="9" r="1" fill="currentColor" />
                    <circle cx="15" cy="9" r="1" fill="currentColor" />
                  </svg>
                  <span>Začít test</span>
                </button>
              </>
            ) : (
              <div className="text-center">
                <h2 id="start-test-title" className="sr-only">Možnosti pokračování testu</h2>
                <p className="text-light-text-secondary dark:text-dark-text-secondary mb-4">
                  Máte rozpracovaný pokus. Můžete pokračovat nebo začít znovu.
                </p>
                <div className="space-y-2">
                  <button
                    onClick={handleResumeTest}
                    className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 focus:bg-green-700 text-white rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    aria-describedby="existing-attempt-title"
                  >
                    Pokračovat v testu
                  </button>
                  <button
                    onClick={handleStartTest}
                    className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 text-white rounded-lg font-poppins font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    aria-describedby="existing-attempt-title"
                  >
                    Začít znovu
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default TestStartPage;